/**
 * The first-run onboarding pop-up's state machine (owner decision 2026-10-09).
 * Pure, so every rule is testable without a DOM:
 *   welcome → goal → experience → role → (save) → done
 * - No close: Escape and a click outside never change the state.
 * - Goal and experience are required (Continue is disabled until answered);
 *   role is the existing optional free-text question. BeHeard has no step that
 *   needs anything external, so no step offers "I'll do this later".
 * - A failed save keeps every answer and shows an inline error with Retry.
 * - Every question can be skipped (owner decision 2026-10-10): "I'll do this
 *   later" moves on without an answer. A skipped goal saves BeHeard's general
 *   path (DEFAULT_INTENT, the server requires one); a skipped experience saves
 *   none (the server's default, beginner); a skipped role saves none and, as
 *   the last question, saves like Continue. Skips are remembered in this
 *   browser (StoredQuestions) so a refresh does not ask them again.
 */
import type { ExperienceLevel, PrimaryIntent } from '../store/api/onboardingApi';

export type ModalStep = 'welcome' | 'goal' | 'experience' | 'role' | 'done';
export const MODAL_STEPS: ModalStep[] = ['welcome', 'goal', 'experience', 'role', 'done'];

export interface ModalState {
  step: ModalStep;
  intent: PrimaryIntent | null;
  experience: ExperienceLevel | null;
  currentRole: string;
  targetRole: string;
  saving: boolean;
  error: string | null;
  /** Questions skipped with "I'll do this later". */
  skipped?: { goal?: boolean; experience?: boolean; role?: boolean };
}

/** The questions: each one offers "I'll do this later". */
export const QUESTION_STEPS: ReadonlySet<ModalStep> = new Set<ModalStep>(['goal', 'experience', 'role']);

/** A skipped goal: BeHeard's general path (the server needs an intent to build one). */
export const DEFAULT_INTENT: PrimaryIntent = 'improve_speaking';

/** What a refresh needs to not ask a skipped question again (this browser only). */
export interface StoredQuestions {
  intent?: PrimaryIntent | 'skipped';
  experience?: ExperienceLevel | 'skipped';
}

export function toStoredQuestions(s: ModalState): StoredQuestions | null {
  if (!s.skipped?.goal && !s.skipped?.experience) return null;
  const out: StoredQuestions = {};
  if (s.skipped?.goal) out.intent = 'skipped';
  else if (s.intent) out.intent = s.intent;
  if (s.skipped?.experience) out.experience = 'skipped';
  else if (s.experience) out.experience = s.experience;
  return out;
}

/** The POST /onboarding/me body: skipped answers fall back to the defaults. */
export function profileBody(s: Pick<ModalState, 'intent' | 'experience' | 'currentRole' | 'targetRole'>) {
  return {
    primaryIntent: s.intent ?? DEFAULT_INTENT,
    experienceLevel: s.experience,
    currentRole: s.currentRole.trim() || null,
    targetRole: s.targetRole.trim() || null,
  };
}

export type ModalAction =
  | { type: 'start' }
  | { type: 'selectIntent'; value: PrimaryIntent }
  | { type: 'selectExperience'; value: ExperienceLevel }
  | { type: 'setRole'; field: 'currentRole' | 'targetRole'; value: string }
  | { type: 'continue' }
  | { type: 'back' }
  /** "I'll do this later" on a question. */
  | { type: 'skip' }
  | { type: 'saveStarted' }
  | { type: 'saveFailed'; message: string }
  | { type: 'saveSucceeded' }
  /** Escape / a click on the scrim: the pop-up cannot be dismissed. */
  | { type: 'dismissAttempt' };

export function initialModalState(started: boolean, stored?: StoredQuestions | null): ModalState {
  const base: ModalState = { step: started ? 'goal' : 'welcome', intent: null, experience: null, currentRole: '', targetRole: '', saving: false, error: null };
  if (!started || !stored) return base;
  const skipped = { goal: stored.intent === 'skipped', experience: stored.experience === 'skipped' };
  const intent = stored.intent && stored.intent !== 'skipped' ? stored.intent : null;
  const experience = stored.experience && stored.experience !== 'skipped' ? stored.experience : null;
  const goalDone = skipped.goal || intent !== null;
  const experienceDone = skipped.experience || experience !== null;
  const step: ModalStep = !goalDone ? 'goal' : !experienceDone ? 'experience' : 'role';
  return { ...base, step, intent, experience, skipped };
}

/** Can Continue move on from this step? Role is optional; the others need an answer. */
export function canContinue(s: ModalState): boolean {
  if (s.saving) return false;
  if (s.step === 'goal') return s.intent !== null;
  if (s.step === 'experience') return s.experience !== null;
  return s.step === 'welcome' || s.step === 'role';
}

/** Steps that may be skipped with "I'll do this later" (only external dependencies). BeHeard: none. */
export const OPTIONAL_STEPS: ReadonlySet<ModalStep> = new Set();

export function modalReducer(s: ModalState, a: ModalAction): ModalState {
  switch (a.type) {
    case 'start':
      return s.step === 'welcome' ? { ...s, step: 'goal' } : s;
    case 'selectIntent':
      return { ...s, intent: a.value, skipped: { ...s.skipped, goal: false } };
    case 'selectExperience':
      return { ...s, experience: a.value, skipped: { ...s.skipped, experience: false } };
    case 'setRole':
      return { ...s, [a.field]: a.value };
    case 'continue':
      if (!canContinue(s)) return s;
      if (s.step === 'welcome') return { ...s, step: 'goal' };
      if (s.step === 'goal') return { ...s, step: 'experience' };
      if (s.step === 'experience') return { ...s, step: 'role' };
      return s; // role → the save (saveStarted); done → close (the component's)
    case 'back':
      if (s.saving) return s;
      if (s.step === 'experience') return { ...s, step: 'goal', error: null };
      if (s.step === 'role') return { ...s, step: 'experience', error: null };
      return s;
    case 'skip':
      if (s.saving) return s;
      if (s.step === 'goal') return { ...s, intent: null, skipped: { ...s.skipped, goal: true }, step: 'experience', error: null };
      if (s.step === 'experience') return { ...s, experience: null, skipped: { ...s.skipped, experience: true }, step: 'role', error: null };
      // Role, the last question: no role is saved; the component saves (saveStarted) like Continue.
      if (s.step === 'role') return { ...s, currentRole: '', targetRole: '', skipped: { ...s.skipped, role: true } };
      return s;
    case 'saveStarted':
      // Each question is answered or skipped by now (a skipped one saves its default).
      return s.step === 'role' ? { ...s, saving: true, error: null } : s;
    case 'saveFailed':
      return { ...s, saving: false, error: a.message };
    case 'saveSucceeded':
      return { ...s, saving: false, error: null, step: 'done' };
    case 'dismissAttempt':
      return s;
    default:
      return s;
  }
}

/** Keyboard inside the pop-up: Escape never closes; Enter continues; arrows move between options. */
export type ModalKey = 'continue' | 'next-option' | 'prev-option' | 'ignore';
export function modalKey(key: string): ModalKey {
  if (key === 'Enter') return 'continue';
  if (key === 'ArrowDown' || key === 'ArrowRight') return 'next-option';
  if (key === 'ArrowUp' || key === 'ArrowLeft') return 'prev-option';
  return 'ignore'; // Escape included
}
